import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from "react";
import { GuidGenerator } from "../Util/Helpers/GuidGenerator";
import { TotalCostsGrossMarginAnnualSales } from "./TotalCostsGrossMarginAnnualSales";
import { TotalYearGrossMarginPerYear } from "./TotalYearGrossMarginPerYear";
import { GetMonthlySalaryPerYear } from "./GetMonthlySalaryPerYear";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

// Create context
const TrialAuthContext = createContext();

// Export context for use in app
export function useTrialAuth() {
  return useContext(TrialAuthContext);
}

export default function TrialAuthProvider({ children }) {
  const dispatch = useDispatch();
  const activeYear = useSelector((state) => state.year);
  const location = useLocation();

  // Mock user
  const currentId = "trial-user";

  // current page based on route
  const getCurrentPage = () => {
    if (location.pathname === "/trial/fastaKostnader") {
      return "Fastakostnader";
    } else if (location.pathname === "/trial") {
      return "Produktmixern";
    }
    return "Produktmixern";
  };
  const currentPage = getCurrentPage();

  // Initialize state, add placeholder data if needed (schablon siffror åtgärder for example)
  //Two rows to get user started
  const [data, setData] = useState({
    data: [
      {
        id: GuidGenerator(),
        user: currentId,
        year: "year1",
        timeStamp: new Date().toISOString(),
        object: {
          category: "Tjänster",
          product: "Tjänst",
          price: "0",
          variableCost: "0",
          volumeYear: "0",
          workHours: "0",
        },
      },
      {
        id: GuidGenerator(),
        user: currentId,
        year: "year1",
        timeStamp: new Date().toISOString(),
        object: {
          category: "Produkter",
          product: "Produkt",
          price: "0",
          variableCost: "0",
          volumeYear: "0",
          workHours: "0",
        },
      },
    ],
  });

  // State for fixed costs (Fastakostnader)
  const [fixedCostData, setFixedCostData] = useState({
    fixedCostData: [
      {
        id: GuidGenerator(),
        user: currentId,
        year: "year1",
        timeStamp: new Date().toISOString(),
        object: {
          category: "Hyra",
          amount: "0",
        },
      },
      {
        id: GuidGenerator(),
        user: currentId,
        year: "year1",
        timeStamp: new Date().toISOString(),
        object: {
          category: "Löner",
          amount: "0",
        },
      },
    ],
  });

  // State for company form (affects salary calculations)
  const [companyFormData, setCompanyFormData] = useState({
    companyFormYear1: "enskildFirma",
    companyFormYear2: "enskildFirma",
    companyFormYear3: "enskildFirma",
  });

  // State for salary calculation
  const [calculating, setCalculating] = useState(false);

  // State for measures (Åtgärder)
  const [measuresData, setMeasuresData] = useState({
    measuresData: [
      {
        id: GuidGenerator(),
        user: currentId,
        timeStamp: new Date().toISOString(),
        object: {
          costs1: 0,
          costs2: 0,
          costs3: 0,
          enddate: "",
          hours1: 0,
          hours2: 0,
          hours3: 0,
          measureName: "",
          priority: "",
          responsible: "",
          startdate: "",
        },
      },
    ],
  });

  // Selectors for salary calculation (must be before function definitions)
  const activeCompany = useSelector((state) => state.companyForm);
  const totalFixedCost = useSelector((state) => state.totalFixedCost);
  const totalYearGrossMargin = useSelector(
    (state) => state.totalYearGrossMargin,
  );
  const totalMeasuredCostsPerYear = useSelector(
    (state) => state.TotalMeasuredCostsYear,
  );
  const standardTaxRelief = useSelector(
    (state) => state.salaryCalculation.standardTaxRelief,
  );
  const stateTaxPercent = useSelector(
    (state) => state.salaryCalculation.stateTaxPercent,
  );
  const generalPensionCutOffValue = useSelector(
    (state) => state.salaryCalculation.generalPensionCutOffValue,
  );
  const generalPensionPercent = useSelector(
    (state) => state.salaryCalculation.generalPensionPercent,
  );
  const templatePayrollPercent = useSelector(
    (state) => state.salaryCalculation.templatePayrollPercent,
  );
  const localTaxesPercent = useSelector(
    (state) => state.salaryCalculation.localTaxesPercent,
  );
  const stateTaxCutOfValue = useSelector(
    (state) => state.salaryCalculation.stateTaxCutOfValue,
  );
  const payrollTaxPercent = useSelector(
    (state) => state.salaryCalculation.payrollTaxPercent,
  );
  const funeralFeesPercent = useSelector(
    (state) => state.salaryCalculation.funeralFeesPercent,
  );
  const earnedIncomeTaxCutOffValue = useSelector(
    (state) => state.salaryCalculation.earnedIncomeTaxCutOffValue,
  );
  const earnedIncomeTaxPercent = useSelector(
    (state) => state.salaryCalculation.earnedIncomeTaxPercent,
  );
  const generalPayrollTaxPercent = useSelector(
    (state) => state.salaryCalculation.generalPayrollTaxPercent,
  );
  const nonTaxableIncomeLimitNegativeNumber = useSelector(
    (state) => state.salaryCalculation.nonTaxableIncomeLimit,
  );
  const nonTaxableIncomeLimit = Math.abs(nonTaxableIncomeLimitNegativeNumber);

  // Call calculation functions to update Redux state
  // These functions contain their own useEffect hooks internally
  TotalCostsGrossMarginAnnualSales(fixedCostData, activeYear, dispatch, data);
  TotalYearGrossMarginPerYear(data, dispatch);

  // Call monthly salary calculation
  GetMonthlySalaryPerYear(
    dispatch,
    calculating,
    templatePayrollPercent,
    standardTaxRelief,
    localTaxesPercent,
    payrollTaxPercent,
    generalPensionPercent,
    generalPensionCutOffValue,
    funeralFeesPercent,
    earnedIncomeTaxCutOffValue,
    earnedIncomeTaxPercent,
    generalPayrollTaxPercent,
    activeCompany,
    activeYear,
    totalFixedCost,
    totalYearGrossMargin,
    totalMeasuredCostsPerYear,
    nonTaxableIncomeLimit,
    stateTaxCutOfValue,
    stateTaxPercent,
    setCalculating,
  );

  // Add new row (post local data) - memoized to prevent unnecessary re-renders
  const postLocalData = useCallback(
    async (object) => {
      if (currentPage === "Produktmixern") {
        const payload = {
          id: GuidGenerator(),
          user: currentId,
          year: activeYear.year,
          timeStamp: new Date().toISOString(),
          object,
        };
        if (data) {
          const newList = [...data.data];
          const updatedList = [...newList.concat(payload)];
          setData({ data: updatedList });
        }
      }
      if (currentPage === "Fastakostnader") {
        const payload = {
          id: GuidGenerator(),
          user: currentId,
          year: activeYear.year,
          timeStamp: new Date().toISOString(),
          object,
        };
        if (fixedCostData) {
          const newList = [...fixedCostData.fixedCostData];
          const updatedList = [...newList.concat(payload)];
          setFixedCostData({ fixedCostData: updatedList });
        }
      }
      if (currentPage === "Åtgärder") {
        const payload = {
          id: GuidGenerator(),
          user: currentId,
          timeStamp: new Date().toISOString(),
          object,
        };
        if (measuresData) {
          const newList = [...measuresData.measuresData];
          const updatedList = [...newList.concat(payload)];
          setMeasuresData({ measuresData: updatedList });
        }
      }
    },
    [
      currentPage,
      currentId,
      activeYear.year,
      data,
      fixedCostData,
      measuresData,
    ],
  );

  // Edit text data - memoized
  const editLocalData = useCallback(
    (e, idCurrentRow) => {
      if (currentPage === "Produktmixern" && data && data.data) {
        let updatedList = data.data.map((item) => {
          if (item.id === idCurrentRow) {
            const object = {
              ...item.object,
              [e.target.name]:
                e.target.value.charAt(0).toUpperCase() +
                e.target.value.slice(1),
            };
            return { ...item, object };
          }
          return item;
        });
        setData({ data: updatedList });
      }
      if (
        currentPage === "Fastakostnader" &&
        fixedCostData &&
        fixedCostData.fixedCostData
      ) {
        let updatedList = fixedCostData.fixedCostData.map((item) => {
          if (item.id === idCurrentRow) {
            const object = {
              ...item.object,
              [e.target.name]:
                e.target.value.charAt(0).toUpperCase() +
                e.target.value.slice(1),
            };
            return { ...item, object };
          }
          return item;
        });
        setFixedCostData({ fixedCostData: updatedList });
      }
      if (
        currentPage === "Åtgärder" &&
        measuresData &&
        measuresData.measuresData
      ) {
        let updatedList = measuresData.measuresData.map((item) => {
          if (item.id === idCurrentRow) {
            const object = {
              ...item.object,
              [e.target.name]:
                e.target.value.charAt(0).toUpperCase() +
                e.target.value.slice(1),
            };
            return { ...item, object };
          }
          return item;
        });
        setMeasuresData({ measuresData: updatedList });
      }
    },
    [currentPage, data, fixedCostData, measuresData],
  );

  // Edit number data - memoized
  const editLocalNumberData = useCallback(
    (e, idCurrentRow) => {
      if (currentPage === "Produktmixern" && data && data.data) {
        if (e.target.value === "") {
          e.target.value = 0;
        }
        let updatedList = data.data.map((item) => {
          if (item.id === idCurrentRow) {
            const object = {
              ...item.object,
              [e.target.name]: parseFloat(e.target.value),
            };
            return { ...item, object };
          }
          return item;
        });
        setData({ data: updatedList });
      }
      if (
        currentPage === "Fastakostnader" &&
        fixedCostData &&
        fixedCostData.fixedCostData
      ) {
        if (e.target.value === "") {
          e.target.value = 0;
        }
        let updatedList = fixedCostData.fixedCostData.map((item) => {
          if (item.id === idCurrentRow) {
            const object = {
              ...item.object,
              [e.target.name]: parseInt(e.target.value),
            };
            return { ...item, object };
          }
          return item;
        });
        setFixedCostData({ fixedCostData: updatedList });
      }
      if (
        currentPage === "Åtgärder" &&
        measuresData &&
        measuresData.measuresData
      ) {
        if (e.target.value === "") {
          e.target.value = 0;
        }
        let updatedList = measuresData.measuresData.map((item) => {
          if (item.id === idCurrentRow) {
            const object = {
              ...item.object,
              [e.target.name]: parseInt(e.target.value),
            };
            return { ...item, object };
          }
          return item;
        });
        setMeasuresData({ measuresData: updatedList });
      }
    },
    [currentPage, data, fixedCostData, measuresData],
  );

  // Delete local data - memoized
  const deleteLocalData = useCallback(
    (id) => {
      if (currentPage === "Produktmixern" && data && data.data) {
        const newList = [...data.data];
        const deleteId = newList.filter((i) => i.id !== id);
        setData({ data: deleteId });
      }
      if (
        currentPage === "Fastakostnader" &&
        fixedCostData &&
        fixedCostData.fixedCostData
      ) {
        const newList = [...fixedCostData.fixedCostData];
        const deleteId = newList.filter((i) => i.id !== id);
        setFixedCostData({ fixedCostData: deleteId });
      }
      if (
        currentPage === "Åtgärder" &&
        measuresData &&
        measuresData.measuresData
      ) {
        const newList = [...measuresData.measuresData];
        const deleteId = newList.filter((i) => i.id !== id);
        setMeasuresData({ measuresData: deleteId });
      }
    },
    [currentPage, data, fixedCostData, measuresData],
  );

  // Function to update company form
  const editCompanyForm = useCallback((updatedCompanyForm) => {
    setCompanyFormData((prev) => ({
      ...prev,
      ...updatedCompanyForm,
    }));
  }, []);

  // Memoize salaryCalculationData to prevent unnecessary re-renders
  const salaryCalculationData = useMemo(
    () => [
      {
        user: currentId,
        object: {
          activeCompany: companyFormData,
        },
      },
    ],
    [companyFormData],
  );

  const value = useMemo(
    () => ({
      data,
      setData,
      fixedCostData,
      setFixedCostData,
      measuresData,
      setMeasuresData,
      editLocalData,
      editLocalNumberData,
      postLocalData,
      deleteLocalData,
      currentUser: { uid: currentId, email: "trial@user.com" },
      isPremium: false, // Trial users are not premium
      isTrialAllowed: true,
      subscriptionActiveStatus: false,
      salaryCalculationData,
      editCompanyForm,
    }),
    [
      data,
      fixedCostData,
      measuresData,
      salaryCalculationData,
      editCompanyForm,
      editLocalData,
      editLocalNumberData,
      postLocalData,
      deleteLocalData,
    ],
  );

  return (
    <TrialAuthContext.Provider value={value}>
      {children}
    </TrialAuthContext.Provider>
  );
}
